import { Layout, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';
import MonthlyRateFootnote from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';
import PriceFootnote from '~/app/compositions/content/calculation-info/components/price-footnote';
import {
	CalculationInfoStyle,
	MonthlyPrice,
	PriceBarTotalPrice,
	TotalPrice,
} from '~/app/compositions/content/calculation-info/styles/calculation-info-style';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { PriceDetailsDialog } from '~/app/pages/summary/dialogs/price-detail';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction, LayerType } from '~/app/tracking/feld-m-types';
import { getMonthlyRateDisclaimer } from '~/app/utils/get-monthly-rate-disclaimer';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { BasicProps } from '~/models/basic-props';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function CalculationInfo({ className }: BasicProps) {
	const { config, formatter } = useMarketConfig();
	const { loadingFlags, catalog, carIdentifier } = useConfigurator();
	const { formatMonthlyRate } = useCurrencyFormatter();
	const showLoadingIndicator = loadingFlags.priceSummary;
	const { i18n } = useI18n();
	const { financingData } = useAppState();
	const totalPrice = catalog.priceSummary?.totalPrice;
	const monthlyRate = catalog.priceSummary?.monthlyRate;
	const [disclaimerText, setDisclaimer] = useAtom(state.financingDisclaimerText);
	const currentView = useAtomValue(state.currentView);
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const {
		featureServices: { footnoteService },
	} = useAppServices();
	footnoteService?.getData();

	useEffect(() => {
		const newDisclaimerText = getMonthlyRateDisclaimer(
			config.financingDisclaimer,
			financingData?.financing?.disclaimers,
		);
		if (newDisclaimerText) {
			setDisclaimer(newDisclaimerText);
		}
	}, [financingData?.financing?.disclaimers]);

	const { openDialog, isDialogOpen, closeDialog } = useDialogs();

	const trackClickEvent = useCallback((eventName: LayerType, label: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (price bar) - click to open ${eventName}`,
			componentName: 'configurator-price-bar',
			label,
		});
	}, []);

	return (
		<CalculationInfoStyle $view={currentView} className={className}>
			{!showLoadingIndicator && (
				<>
					{config.showPrices && (
						<div
							className={'calculation-info total-price'}
							data-testid={DATA_TEST_ID.TOTAL_PRICE_CONTAINER}>
							<PriceBarTotalPrice>
								<Text
									tag={'p'}
									variant={{ type: 'copy', order: '2', style: 'normal' }}
									className="calculation-info-item">
									{i18n('ngc.priceDetails.totalPrice')}
								</Text>
								<Layout>
									<TotalPrice>{formatter.formatPrice(totalPrice?.value)}</TotalPrice>
									<Text
										tag={'p'}
										variant={{ type: 'copy', order: '1', style: 'bold' }}
										className="calculation-info-item">
										{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}
									</Text>
								</Layout>
							</PriceBarTotalPrice>

							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('priceDetails');
									trackClickEvent(LayerType.PriceDetailsLayer, i18n('ngc.actions.showPriceDetails'));
								}}
								size={'sm'}
								variant={'standalone'}
								data-testid={DATA_TEST_ID.PRICE_DETAILS_BUTTON}>
								{i18n('ngc.actions.showPriceDetails')}
							</TextLink>

							<PriceDetailsDialog
								open={isDialogOpen('priceDetails')}
								onClose={() => {
									appTrackingService.summaryClickEvent({
										eventAction: EventAction.InternalLink,
										eventName: `configurator (${LayerType.PriceDetailsLayer}) - click to close layer`,
										componentName: 'price-details-layer',
									});
									appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'close');
									appTrackingService.viewChangeEvent({
										eventName: `generic`,
										componentName: 'price-details-layer',
									});
									closeDialog('priceDetails');
								}}
							/>
						</div>
					)}
					{monthlyRate && config.enableFinancingServices && (
						<div
							className="calculation-info monthly-price"
							data-testid={DATA_TEST_ID.MONTHLY_RATE_CONTAINER}>
							<MonthlyPrice>
								{formatMonthlyRate(monthlyRate)}
								<MonthlyRateFootnote text={disclaimerText} />
							</MonthlyPrice>

							<TextLink
								className="text-grey"
								onClick={() => {
									openDialog('monthlyPriceRatesFsag');
									trackClickEvent(
										LayerType.FinancingOptionsLayer,
										i18n('ngc.actions.changeLeasingRate'),
									);
								}}
								size={'sm'}
								variant={'standalone'}
								data-testid={DATA_TEST_ID.FINANCING_LAYER_BUTTON}>
								{i18n('ngc.actions.changeLeasingRate')}
							</TextLink>
						</div>
					)}
					{monthlyRate && config.enableFinancingServices && (
						<FsagMonthlyPriceRateDialog
							open={isDialogOpen('monthlyPriceRatesFsag')}
							onClose={() => closeDialog('monthlyPriceRatesFsag')}
						/>
					)}
				</>
			)}

			{showLoadingIndicator && config.showPrices && (
				<Text
					tag={'p'}
					variant={{ type: 'copy', order: '1', style: 'bold' }}
					className="calculation-info-item loading">
					{i18n('ngc.currency.calculatingPrice')}...
				</Text>
			)}
		</CalculationInfoStyle>
	);
}
